// ---------------------------------------------------------------------------
// Validation and normalization.
//
// Everything here is a pure, total function of its inputs. No clocks, no
// randomness, no I/O. `normalizeWord` in particular runs on both the dictionary
// build side and the answer-checking side, so any drift between the two would
// silently reject correct words.
// ---------------------------------------------------------------------------
import { CHAT_MESSAGE_MAX, DICTIONARY_SIZES, DISPLAY_NAME_MAX, DISPLAY_NAME_MIN, MAX_PLAYERS_HARD_CAP, MAX_SPECTATORS_CAP, RESERVED_DISPLAY_NAMES, RESERVED_USERNAMES, REVEAL_PACE_MAX, REVEAL_PACE_MIN, ROUND_SEQUENCE_MAX, USERNAME_MAX, USERNAME_MIN, } from './constants.js';
// --- Local bounds ----------------------------------------------------------
//
// Not exported: `index.ts` re-exports every module with `export *`, so adding
// names here risks colliding with another module's constants. Anything the rest
// of the app needs to agree on belongs in constants.ts instead.
/** No clock shorter than this is playable, and none longer is a game. */
const TIMER_MIN_SECONDS = 5;
const TIMER_MAX_SECONDS = 300;
const CONUNDRUM_LENGTH_MIN = 6;
const CONUNDRUM_LENGTH_MAX = 9;
/** A room that admits one player is a room nobody can join. */
const MAX_PLAYERS_MIN = 2;
const MAX_SPECTATORS_MIN = 0;
// --- Character classes -----------------------------------------------------
/** C0 and C1 control characters. Never legitimate in a name or a chat line. */
const CONTROL_CHARS_SOURCE = '[\\u0000-\\u001f\\u007f-\\u009f]';
/**
 * Bidi controls and explicit overrides. A real impersonation vector: an RLO lets
 * a name render as a completely different string from the one that is stored.
 */
const BIDI_CONTROLS_SOURCE = '[\\u200e\\u200f\\u202a-\\u202e\\u2061-\\u2064\\u2066-\\u2069]';
/**
 * Invisible characters that would otherwise let a name be blank, or let two
 * distinct names render identically.
 */
const INVISIBLE_CHARS_SOURCE = '[\\u00ad\\u180e\\u200b\\u200c\\u200d\\u2060\\u2800\\u3164\\ufeff\\uffa0]';
const CONTROL_CHARS = new RegExp(CONTROL_CHARS_SOURCE);
const CONTROL_CHARS_GLOBAL = new RegExp(CONTROL_CHARS_SOURCE, 'g');
const BIDI_CONTROLS = new RegExp(BIDI_CONTROLS_SOURCE);
const BIDI_CONTROLS_GLOBAL = new RegExp(BIDI_CONTROLS_SOURCE, 'g');
// `no-misleading-character-class` is suppressed here on purpose, and the
// suppression is on this line rather than on the string above because the rule
// resolves the constant at the point the RegExp is built.
//
// It objects that U+200D ZERO WIDTH JOINER sits between U+200C and U+2060 in
// the class, and reads that as three code points meant to be ONE grapheme —
// which in a class matching emoji would be a real bug, because the class would
// then match half a character. This class is not matching graphemes. It is a
// set of code points to strip one at a time, and the joiner is in it because it
// is one of the characters a display name must not be built out of, not because
// anything here is meant to join.
//
// Do not "fix" it by reordering the escapes so the joiner stops sitting between
// two others. That silences the rule by accident, and the next person to add a
// character to the set re-breaks it with nothing to tell them why.
// eslint-disable-next-line no-misleading-character-class
const INVISIBLE_CHARS_GLOBAL = new RegExp(INVISIBLE_CHARS_SOURCE, 'g');
/** Combining marks left behind by an NFD decomposition. */
const COMBINING_MARKS_GLOBAL = new RegExp('[\\u0300-\\u036f]', 'g');
/** At least one letter, digit, or pictographic symbol. Not pure punctuation. */
const HAS_SUBSTANCE = /[\p{L}\p{N}\p{So}]/u;
const USERNAME_CHARSET = /^[a-z0-9_]+$/;
/**
 * Letters that survive NFD intact because they are ligatures or barred forms
 * rather than accented ones. Small and deliberate: these are the ones that turn
 * up in real word lists and real names.
 */
const LETTER_EXPANSIONS = {
    'Æ': 'AE', // AE
    'Œ': 'OE', // OE
    'Ø': 'O', // O with stroke
    'Ł': 'L', // L with stroke
    'Đ': 'D', // D with stroke
    'Ð': 'D', // Eth
    'Þ': 'TH', // Thorn
    'Ħ': 'H', // H with stroke
    'Ŋ': 'NG', // Eng
    'ı': 'I', // dotless i
};
// --- Names -----------------------------------------------------------------
/**
 * Free text, 1 to 24 characters after cleaning, parentheses explicitly allowed.
 *
 * Parentheses matter: a guest calling themselves `Hyper (isaac)` is a legal
 * name, and the UI defends against the impersonation by rendering the real
 * `@username` as a structurally separate element rather than by banning
 * characters that ordinary players want.
 */
export function validateDisplayName(name) {
    if (typeof name !== 'string') {
        return { ok: false, error: 'Display name must be text.' };
    }
    if (CONTROL_CHARS.test(name)) {
        return { ok: false, error: 'Display name cannot contain control characters.' };
    }
    if (BIDI_CONTROLS.test(name)) {
        return { ok: false, error: 'Display name cannot contain text-direction characters.' };
    }
    const value = name.replace(INVISIBLE_CHARS_GLOBAL, '').replace(/\s+/g, ' ').trim();
    const length = Array.from(value).length;
    if (length < DISPLAY_NAME_MIN) {
        return { ok: false, error: `Display name must be at least ${DISPLAY_NAME_MIN} character long.` };
    }
    if (length > DISPLAY_NAME_MAX) {
        return { ok: false, error: `Display name must be at most ${DISPLAY_NAME_MAX} characters long.` };
    }
    if (!HAS_SUBSTANCE.test(value)) {
        return { ok: false, error: 'Display name must contain at least one letter or number.' };
    }
    if (isReservedDisplayName(value)) {
        return { ok: false, error: `"${value.trim()}" is reserved. Please pick another name.` };
    }
    return { ok: true, value };
}
/**
 * Whether a display name claims to be part of the room rather than a person in
 * it. See `RESERVED_DISPLAY_NAMES` for what belongs on the list.
 *
 * **The folding is the whole of it, and it is deliberately narrow.** Lowercase,
 * punctuation dropped, whitespace collapsed, and a leading `the` removed — so
 * `The Audience`, `audience`, `AUDIENCE!` and `the  audience` are one name and
 * are all refused. That covers somebody typing the obvious thing, which is what
 * this is for.
 *
 * **It does not chase a determined impersonator, on purpose.** `Aud1ence`,
 * `Αudience` with a Greek alpha and `A u d i e n c e` all get through, and
 * trying to catch them means confusable-script folding and character-gap
 * matching — which is the substring matcher that `nameFilter.ts` exists to warn
 * against, aimed at a smaller problem. The costly failure here is the reverse
 * one: refusing a real name because it happens to fold onto a reserved word.
 * Somebody genuinely called `Mod` is rare; a filter that told half a room their
 * name was reserved would be a bug everybody meets.
 *
 * The room has a real answer to a determined impersonator anyway, and it is not
 * a string check: registered accounts carry a username, and the roster draws it
 * beside the display name.
 */
export function isReservedDisplayName(name) {
    const folded = name
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, '')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^the\s+/, '');
    return RESERVED_DISPLAY_NAMES.includes(folded);
}
/**
 * Make a display name unique in a room, by suffixing the *newcomer*.
 *
 * Two Isaacs in one room is not a tie to be refused, it is a collision to be
 * resolved, and the resolution has a direction: whoever is already wearing the
 * name keeps it, and the person who just asked for it becomes `Isaac (2)`, then
 * `Isaac (3)`. Refusing instead — which is what `rename` did, and what joining
 * did not do at all — makes the room tell a player their own name is not
 * available, on a screen where they cannot see who has it.
 *
 * Comparison is case-insensitive and whitespace-folded, because `isaac` and
 * `Isaac` are the same name to everyone reading the running order. It is the
 * *compared* form that is folded; the name kept is exactly what was asked for.
 *
 * **The suffix has to fit.** `DISPLAY_NAME_MAX` is 24 code points and a name at
 * the limit plus ` (2)` is 28, which `validateDisplayName` would then reject —
 * so the base is trimmed from the right to make room rather than the suffix
 * being dropped. Trailing space is trimmed after the cut, so a base ending mid-
 * word does not come out as `Bartholomew Cubbi (2)` with two spaces in it.
 *
 * `taken` is every other name in the room. Callers pass the room's names minus
 * their own, or a rename to the name you already have would suffix you.
 */
export function uniqueDisplayName(desired, taken) {
    const fold = (name) => name.replace(/\s+/g, ' ').trim().toLowerCase();
    const used = new Set();
    for (const name of taken)
        used.add(fold(name));
    if (!used.has(fold(desired)))
        return desired;
    // Two is where a duplicate starts: the first one is not "(1)".
    for (let n = 2; n < 1_000; n++) {
        const suffix = ` (${n})`;
        const room = DISPLAY_NAME_MAX - suffix.length;
        const chars = Array.from(desired);
        const base = (chars.length > room ? chars.slice(0, room).join('') : desired).trimEnd();
        const candidate = `${base}${suffix}`;
        if (!used.has(fold(candidate)))
            return candidate;
    }
    // Unreachable with any room this game can seat — the cap is sixteen — but a
    // loop with no exit is worse than a name nobody asked for.
    return desired;
}
/**
 * Permanent, lowercase, `[a-z0-9_]`, 3 to 16 characters, not reserved.
 *
 * Uppercase input is rejected rather than quietly lowercased. The username is
 * permanent and is the anti-impersonation anchor, so the user has to see exactly
 * what they are getting before it is fixed for good.
 */
export function validateUsername(name) {
    if (typeof name !== 'string') {
        return { ok: false, error: 'Username must be text.' };
    }
    const value = name.trim();
    if (value.length === 0) {
        return { ok: false, error: 'Username cannot be empty.' };
    }
    if (value !== value.toLowerCase()) {
        return { ok: false, error: 'Username must be lowercase. Try ' + value.toLowerCase() + '.' };
    }
    if (value.length < USERNAME_MIN) {
        return { ok: false, error: `Username must be at least ${USERNAME_MIN} characters long.` };
    }
    if (value.length > USERNAME_MAX) {
        return { ok: false, error: `Username must be at most ${USERNAME_MAX} characters long.` };
    }
    if (!USERNAME_CHARSET.test(value)) {
        return { ok: false, error: 'Username can only use letters a to z, numbers, and underscores.' };
    }
    if (RESERVED_USERNAMES.includes(value)) {
        return { ok: false, error: `"${value}" is reserved. Please pick another username.` };
    }
    return { ok: true, value };
}
// --- Settings --------------------------------------------------------------
function isRoundType(value) {
    return value === 'letters' || value === 'numbers' || value === 'conundrum';
}
/** Round to an integer inside [min, max], or null when the input is not a number. */
function clampInt(value, min, max) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    return Math.min(max, Math.max(min, Math.round(value)));
}
function asBoolean(value) {
    return typeof value === 'boolean' ? value : null;
}
/** Snap to the nearest offered SCOWL band rather than rejecting outright. */
function nearestDictionarySize(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        return null;
    let best = DICTIONARY_SIZES[0];
    for (const size of DICTIONARY_SIZES) {
        if (Math.abs(size - value) < Math.abs(best - value))
            best = size;
    }
    return best;
}
/**
 * Clamp and sanity-check a partial settings patch. Drops unknown keys.
 *
 * Only keys present in the patch appear in the result, so the caller can merge
 * it straight over the current settings. A present-but-unusable value (wrong
 * type, NaN, an all-invalid round sequence) is dropped rather than guessed at.
 *
 * Note what is deliberately absent: nothing forces a conundrum into the last
 * position, or forces one to exist at all, and a sequence may hold several.
 */
export function sanitizeSettings(patch, current) {
    const out = {};
    if ('roundSequence' in patch && Array.isArray(patch.roundSequence)) {
        const cleaned = patch.roundSequence.filter(isRoundType).slice(0, ROUND_SEQUENCE_MAX);
        if (cleaned.length > 0)
            out.roundSequence = cleaned;
    }
    if ('lettersSelectionSeconds' in patch) {
        const v = clampInt(patch.lettersSelectionSeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.lettersSelectionSeconds = v;
    }
    if ('lettersPlaySeconds' in patch) {
        const v = clampInt(patch.lettersPlaySeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.lettersPlaySeconds = v;
    }
    if ('numbersSelectionSeconds' in patch) {
        const v = clampInt(patch.numbersSelectionSeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.numbersSelectionSeconds = v;
    }
    if ('numbersPlaySeconds' in patch) {
        const v = clampInt(patch.numbersPlaySeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.numbersPlaySeconds = v;
    }
    if ('conundrumSeconds' in patch) {
        const v = clampInt(patch.conundrumSeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.conundrumSeconds = v;
    }
    if ('conundrumBuzzAnswerSeconds' in patch) {
        const v = clampInt(patch.conundrumBuzzAnswerSeconds, TIMER_MIN_SECONDS, TIMER_MAX_SECONDS);
        if (v !== null)
            out.conundrumBuzzAnswerSeconds = v;
    }
    if ('conundrumLength' in patch) {
        const v = clampInt(patch.conundrumLength, CONUNDRUM_LENGTH_MIN, CONUNDRUM_LENGTH_MAX);
        if (v !== null)
            out.conundrumLength = v;
    }
    if ('maxPlayers' in patch) {
        // `null` is a value here, not an omission: it is what "no limit" is stored
        // as and it is the default, so it has to be settable back after somebody has
        // typed a number in. `clampInt` would read it as garbage and drop it,
        // leaving a host who cleared the box with the cap they were trying to
        // remove.
        if (patch.maxPlayers === null) {
            out.maxPlayers = null;
        }
        else {
            const v = clampInt(patch.maxPlayers, MAX_PLAYERS_MIN, MAX_PLAYERS_HARD_CAP);
            if (v !== null)
                out.maxPlayers = v;
        }
    }
    if ('maxSpectators' in patch) {
        // Null is a value, exactly as it is for `maxPlayers` — "no limit" rather
        // than "unset". `clampInt` answers null for both, so the two have to be
        // told apart before it is asked.
        if (patch.maxSpectators === null) {
            out.maxSpectators = null;
        }
        else {
            const v = clampInt(patch.maxSpectators, MAX_SPECTATORS_MIN, MAX_SPECTATORS_CAP);
            if (v !== null)
                out.maxSpectators = v;
        }
    }
    if ('dictionarySize' in patch) {
        const v = nearestDictionarySize(patch.dictionarySize);
        if (v !== null)
            out.dictionarySize = v;
    }
    if ('visibility' in patch && (patch.visibility === 'public' || patch.visibility === 'private')) {
        out.visibility = patch.visibility;
    }
    if ('roomLock' in patch &&
        (patch.roomLock === 'open' || patch.roomLock === 'players' || patch.roomLock === 'room')) {
        out.roomLock = patch.roomLock;
    }
    if ('playerPermissions' in patch) {
        const v = asBoolean(patch.playerPermissions);
        if (v !== null)
            out.playerPermissions = v;
    }
    if ('allowTeamSelfSelect' in patch) {
        const v = asBoolean(patch.allowTeamSelfSelect);
        if (v !== null)
            out.allowTeamSelfSelect = v;
    }
    if ('conundrumBuzzer' in patch) {
        const v = asBoolean(patch.conundrumBuzzer);
        if (v !== null)
            out.conundrumBuzzer = v;
    }
    if ('audienceSeat' in patch) {
        const v = asBoolean(patch.audienceSeat);
        if (v !== null)
            out.audienceSeat = v;
    }
    if ('audienceCanWin' in patch) {
        const v = asBoolean(patch.audienceCanWin);
        if (v !== null)
            out.audienceCanWin = v;
    }
    if ('chatMode' in patch &&
        (patch.chatMode === 'everyone' || patch.chatMode === 'players' || patch.chatMode === 'off')) {
        out.chatMode = patch.chatMode;
    }
    if ('aggregateReveal' in patch &&
        (patch.aggregateReveal === 'auto' ||
            patch.aggregateReveal === 'on' ||
            patch.aggregateReveal === 'off')) {
        out.aggregateReveal = patch.aggregateReveal;
    }
    if ('audienceAggregation' in patch &&
        (patch.audienceAggregation === 'median' ||
            patch.audienceAggregation === 'mean' ||
            patch.audienceAggregation === 'p75' ||
            patch.audienceAggregation === 'best')) {
        out.audienceAggregation = patch.audienceAggregation;
    }
    if ('pauseOnDisconnect' in patch) {
        const v = asBoolean(patch.pauseOnDisconnect);
        if (v !== null)
            out.pauseOnDisconnect = v;
    }
    if ('allowSpectatorPlayAlong' in patch) {
        const v = asBoolean(patch.allowSpectatorPlayAlong);
        if (v !== null)
            out.allowSpectatorPlayAlong = v;
    }
    if ('llmPrefersRealWords' in patch) {
        const v = asBoolean(patch.llmPrefersRealWords);
        if (v !== null)
            out.llmPrefersRealWords = v;
    }
    if ('lettersWordCheck' in patch) {
        const v = asBoolean(patch.lettersWordCheck);
        if (v !== null)
            out.lettersWordCheck = v;
    }
    if ('hostRulings' in patch) {
        const v = asBoolean(patch.hostRulings);
        if (v !== null)
            out.hostRulings = v;
    }
    if ('revealPacePercent' in patch) {
        const v = clampInt(patch.revealPacePercent, REVEAL_PACE_MIN, REVEAL_PACE_MAX);
        if (v !== null)
            out.revealPacePercent = v;
    }
    // Final gate: the patch must leave the room in a valid state. The clamping
    // above should make this unreachable, which is exactly why it is worth
    // asserting rather than assuming.
    if (!validateSettings({ ...current, ...out }).ok)
        return {};
    return out;
}
export function validateSettings(settings) {
    if (!Array.isArray(settings.roundSequence) || settings.roundSequence.length === 0) {
        return { ok: false, error: 'A game needs at least one round.' };
    }
    if (settings.roundSequence.length > ROUND_SEQUENCE_MAX) {
        return { ok: false, error: `A game can have at most ${ROUND_SEQUENCE_MAX} rounds.` };
    }
    if (!settings.roundSequence.every(isRoundType)) {
        return { ok: false, error: 'Round sequence contains an unknown round type.' };
    }
    const timers = [
        ['Letters selection time', settings.lettersSelectionSeconds],
        ['Letters play time', settings.lettersPlaySeconds],
        ['Numbers selection time', settings.numbersSelectionSeconds],
        ['Numbers play time', settings.numbersPlaySeconds],
        ['Conundrum time', settings.conundrumSeconds],
        ['Conundrum buzz answer time', settings.conundrumBuzzAnswerSeconds],
    ];
    for (const [label, seconds] of timers) {
        if (!Number.isInteger(seconds) || seconds < TIMER_MIN_SECONDS || seconds > TIMER_MAX_SECONDS) {
            return {
                ok: false,
                error: `${label} must be between ${TIMER_MIN_SECONDS} and ${TIMER_MAX_SECONDS} seconds.`,
            };
        }
    }
    if (!Number.isInteger(settings.conundrumLength) ||
        settings.conundrumLength < CONUNDRUM_LENGTH_MIN ||
        settings.conundrumLength > CONUNDRUM_LENGTH_MAX) {
        return {
            ok: false,
            error: `Conundrum length must be between ${CONUNDRUM_LENGTH_MIN} and ${CONUNDRUM_LENGTH_MAX}.`,
        };
    }
    // `null` means no limit and is the default. Anything else has to be a real
    // integer inside the bounds; `undefined` is accepted for the same reason
    // `hostRulings` is below — a room restored from a snapshot written before this
    // setting existed comes back without it, and the safe reading is the default.
    if (settings.maxPlayers !== null && settings.maxPlayers !== undefined) {
        if (!Number.isInteger(settings.maxPlayers) ||
            settings.maxPlayers < MAX_PLAYERS_MIN ||
            settings.maxPlayers > MAX_PLAYERS_HARD_CAP) {
            return {
                ok: false,
                error: `Max players must be between ${MAX_PLAYERS_MIN} and ${MAX_PLAYERS_HARD_CAP}, or left unset.`,
            };
        }
    }
    // Null is no limit and is the default, exactly as for `maxPlayers` above.
    if (settings.maxSpectators !== null && settings.maxSpectators !== undefined) {
        if (!Number.isInteger(settings.maxSpectators) ||
            settings.maxSpectators < MAX_SPECTATORS_MIN ||
            settings.maxSpectators > MAX_SPECTATORS_CAP) {
            return {
                ok: false,
                error: `Max spectators must be between ${MAX_SPECTATORS_MIN} and ${MAX_SPECTATORS_CAP}, or left unset.`,
            };
        }
    }
    if (!DICTIONARY_SIZES.includes(settings.dictionarySize)) {
        return { ok: false, error: 'Unknown dictionary size.' };
    }
    if (settings.visibility !== 'public' && settings.visibility !== 'private') {
        return { ok: false, error: 'Room visibility must be public or private.' };
    }
    if (typeof settings.pauseOnDisconnect !== 'boolean' ||
        typeof settings.allowSpectatorPlayAlong !== 'boolean' ||
        typeof settings.lettersWordCheck !== 'boolean' ||
        typeof settings.llmPrefersRealWords !== 'boolean' ||
        // Not `!== 'boolean'`: a room restored from a snapshot written before this
        // setting existed has it undefined, and `gameCountsForStats` reads undefined as
        // "off", which is the safe direction and the direction the room was in.
        (settings.hostRulings !== undefined && typeof settings.hostRulings !== 'boolean')) {
        return { ok: false, error: 'Toggle settings must be true or false.' };
    }
    if (settings.revealPacePercent !== undefined &&
        (!Number.isInteger(settings.revealPacePercent) ||
            settings.revealPacePercent < REVEAL_PACE_MIN ||
            settings.revealPacePercent > REVEAL_PACE_MAX)) {
        return {
            ok: false,
            error: `Reveal pace must be between ${REVEAL_PACE_MIN} and ${REVEAL_PACE_MAX} percent.`,
        };
    }
    return { ok: true };
}
// --- Chat ------------------------------------------------------------------
export function validateChatBody(body) {
    if (typeof body !== 'string') {
        return { ok: false, error: 'Message must be text.' };
    }
    const value = body
        .replace(INVISIBLE_CHARS_GLOBAL, '')
        .replace(BIDI_CONTROLS_GLOBAL, '')
        .replace(CONTROL_CHARS_GLOBAL, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (value.length === 0) {
        return { ok: false, error: 'Message cannot be empty.' };
    }
    if (Array.from(value).length > CHAT_MESSAGE_MAX) {
        return { ok: false, error: `Message must be at most ${CHAT_MESSAGE_MAX} characters long.` };
    }
    return { ok: true, value };
}
/**
 * Parse a leading /global, /g, /team or /t out of a chat line.
 *
 * Returns null when there is no prefix, so the caller keeps whatever channel the
 * composer is currently on. A bare `/g` with no body is a channel switch, which
 * is why an empty remainder is still a successful parse.
 */
export function parseChatChannel(body) {
    if (typeof body !== 'string')
        return null;
    const match = /^\/(global|g|team|t)(?=\s|$)\s*([\s\S]*)$/i.exec(body.trimStart());
    if (!match)
        return null;
    const keyword = (match[1] ?? '').toLowerCase();
    const channel = keyword === 'g' || keyword === 'global' ? 'global' : 'team';
    return { channel, body: (match[2] ?? '').trim() };
}
// --- Words -----------------------------------------------------------------
/**
 * Uppercase, strip accents, drop apostrophes and hyphens, discard everything
 * that is not A to Z.
 *
 * Total and deterministic by construction: the same function runs over the
 * dictionary at build time and over a player's answer at judging time, so any
 * input at all maps to a comparable key. `CAFE` with an acute, `cafe`, and
 * `Café` all become `CAFE`; `don't` becomes `DONT`; `co-op` becomes `COOP`.
 */
export function normalizeWord(word) {
    if (typeof word !== 'string')
        return '';
    const upper = word.toUpperCase().normalize('NFD').replace(COMBINING_MARKS_GLOBAL, '');
    let expanded = '';
    for (const ch of upper) {
        expanded += LETTER_EXPANSIONS[ch] ?? ch;
    }
    return expanded.replace(/[^A-Z]/g, '');
}
/**
 * Whether `word` can be built from `tiles`, respecting letter multiplicity.
 *
 * The multiplicity check is the whole point: LETTER needs two Ts, and a board
 * holding one T has to reject it.
 */
export function canBuildFromTiles(word, tiles) {
    const normalized = normalizeWord(word);
    if (normalized.length === 0)
        return false;
    const available = new Map();
    for (const tile of Array.isArray(tiles) ? tiles : []) {
        for (const letter of normalizeWord(tile)) {
            available.set(letter, (available.get(letter) ?? 0) + 1);
        }
    }
    for (const letter of normalized) {
        const left = available.get(letter) ?? 0;
        if (left === 0)
            return false;
        available.set(letter, left - 1);
    }
    return true;
}
